/**
 * A stable identity for one feature, so two parts of the app can agree on it.
 *
 * The Query tool hands the map and the attribute table the same features, but
 * each of them fetched its own copy: the table numbered rows by their position
 * in ITS request, the query by position in its own. Row 4 in one is not row 4
 * in the other the moment a filter, a page size or a service ordering differs,
 * so position cannot be the identity.
 *
 * The service's own key is used where there is one. ArcGIS publishes OBJECTID,
 * GeoServer's WFS puts a typeName.fid on the feature itself, and a file-backed
 * layer may carry either. Only when none of those exist does this fall back to
 * the attributes, which is stable for the same reason a database would call it
 * a natural key — and which two identical rows would share, so it is the last
 * resort rather than the first.
 */

/** Field names services use for their primary key, in order of preference. */
const ID_FIELDS = ['OBJECTID', 'objectid', 'OBJECTID_1', 'FID', 'fid', 'GlobalID', 'globalid', 'id'];

/**
 * @param layerId Scopes the key to its layer, and is NOT optional in spirit.
 *
 * Every layer numbers its own features from 1, so `OBJECTID:1801` exists in a
 * pipeline layer, a survey layer and a depth layer at once. Without the scope
 * those three are the same key: selecting 15 pipes and opening the pipeline
 * table ticked 161 rows, because every OBJECTID from every OTHER layer's
 * selection also matched a pipe that happened to share the number.
 */
export function featureKey(
  feature: {
    id?: string | number;
    properties?: Record<string, unknown> | null;
  },
  layerId = '',
): string {
  const properties = feature.properties ?? {};
  const scope = layerId ? `${layerId}::` : '';

  for (const field of ID_FIELDS) {
    const value = properties[field];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return `${scope}${field}:${value}`;
    }
  }

  // WFS puts the feature id beside the properties rather than inside them.
  if (feature.id !== undefined && feature.id !== null && String(feature.id).trim() !== '') {
    return `${scope}fid:${feature.id}`;
  }

  /* No key published. Hashing the attributes keeps this short — a wide layer's
     full JSON as a Map key would hold the whole table in memory twice. */
  const json = JSON.stringify(properties);
  let hash = 5381;
  for (let index = 0; index < json.length; index += 1) {
    hash = ((hash << 5) + hash + json.charCodeAt(index)) | 0;
  }
  return `${scope}attrs:${(hash >>> 0).toString(36)}`;
}
